import {InputSignal, OutputEmitterRef} from '@angular/core';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnChanges, OnDestroy, SimpleChanges, inject, output, input} from '@angular/core';
import {DialogModule} from '@syncfusion/ej2-angular-popups';
import {LinkComponent} from "../link/link.component";
import {TranslatePipe} from "../../pipes/translate.pipe";
import {DatePipe} from "@angular/common";
import {ContentComponent} from "../content/content.component";
import {IconBadgeComponent} from "../icon-badge/icon-badge.component";
import {AvatarComponent} from "../avatar/avatar.component";
import {PillComponent} from "../pill/pill.component";
import {RouterService} from "../../services/router.service";
import {interval, Subscription, take} from "rxjs";
import {NotificationResource} from '../../types/generated/notificationResource';
import {ModuleConfigurationService} from '../../services/module-configuration.service';
import {GenericModelType} from '../../types/generated/genericModelType';
import {FormButtonComponent} from "../form-button/form-button.component";
import {HeadlineComponent} from '../headline/headline.component';
import {ApiService} from '../../services/api.service';
import {CalendarEventConfirmation} from '../../types/generated/calendarEventConfirmation';
import {NotificationActionType} from '../../types/generated/notificationActionType';
import {PendingCalendarEventOpenService} from '../../services/pending-calendar-event-open.service';
import {ToasterService} from '../../services/toaster.service';
import {LanguageService} from '../../services/language.service';
import {NotificationStateService} from '../../services/notification-state.service';
import {CapabilitiesService} from '../../services/capabilities.service';
import {NotificationChannel} from '../../types/generated/notificationChannel';

@Component({
    selector: 'app-notification',
    imports: [
    LinkComponent,
    TranslatePipe,
    ContentComponent,
    DatePipe,
    IconBadgeComponent,
    AvatarComponent,
    PillComponent,
    FormButtonComponent,
    DialogModule,
    HeadlineComponent
],
    templateUrl: './notification.component.html',
    styleUrl: './notification.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
      '(mouseenter)': 'onEnter()',
      '(mouseleave)': 'onLeave()'
    }
})
export class NotificationComponent implements OnChanges, OnDestroy {
  private routerService: RouterService = inject(RouterService);
  private modConfigService: ModuleConfigurationService = inject(ModuleConfigurationService);
  private api: ApiService = inject(ApiService);
  private pendingCalendarEventOpenService: PendingCalendarEventOpenService = inject(PendingCalendarEventOpenService);
  private toaster: ToasterService = inject(ToasterService);
  private lang: LanguageService = inject(LanguageService);
  private notificationStateService: NotificationStateService = inject(NotificationStateService);
  private capabilitiesService: CapabilitiesService = inject(CapabilitiesService);
  private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  /** Accept a single notification (this is for app-bar) test*/

  public notification: InputSignal<NotificationResource | null | undefined> = input<NotificationResource | null | undefined>(undefined);
  /** Accept an array (this is for home to rotate the notifications) */

  public notifications: InputSignal<NotificationResource[] | null | undefined> = input<NotificationResource[] | null | undefined>(undefined);

  /** Auto-advance every N ms (only if there's >1 item) */
  public intervalMs: InputSignal<number> = input<number>(5000);
  /**first N notification */
  public topN: InputSignal<number> = input<number>(5);

  /**Separate the same component out for cases when it is used in the page or in the notification list */

  public variant: InputSignal<'fixed' | 'compact'> = input<'fixed' | 'compact'>('fixed');

  /**Filter out the onse which are marked important */
  public favorImportant: InputSignal<boolean> = input<boolean>(true);
  public asRead: OutputEmitterRef<number> = output<number>();
  public asUnread: OutputEmitterRef<number> = output<number>();
  /** Oringal source */
  private _source: NotificationResource[] = [];
  /** Current index into notifications */
  private _notifications: NotificationResource[] = [];
  public index: number = 0;
  public dialogNotification: NotificationResource | null = null;

  private rotateSub?: Subscription;
  private paused: boolean = false;

  private subscriptions: Subscription[] = [];

  //TODO a save method is required in this component which will save the read notifications but this component also needs a better service which manages the marked read notifications in here for both event when somebody is marking in the bar-top or in the home page the read notifications!


  /** Current notification (or null) */
  public get current(): NotificationResource | null {
    return this._notifications.length ? this._notifications[this.index] : null;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['notifications']) {
      const notifications = this.notifications();
      this._source = Array.isArray(notifications) ? notifications : [];
      this.reGenerate();
    } else if (changes['notification']) {
      const notification = this.notification();
      this._source = notification ? [notification] : [];
      this.reGenerate();
    }
  }

  public notifcationIsCalendarEventOpenInvitation(): boolean {
    if (this.current === null) {
      return false;
    } else {
      const actionPayload = this.current.action?.payload;

      return !!(
        this.current.model?.type === GenericModelType.CALENDAR_EVENT &&
        this.current.action?.type === NotificationActionType.CALENDAR_EVENT_CONFIRMATION &&
        this.isRecord(actionPayload) &&
        actionPayload['answer'] === null
      );
    }
  }

  public isCalendarEventCapacityLimitReached(notification: NotificationResource): boolean {
    const actionPayload = notification.action?.payload;

    return this.isRecord(actionPayload) && actionPayload['capacity_limit_reached'] === true;
  }

  /** Navigation */
  public next(): void {
    if (!this._notifications.length) return;
    this.index = (this.index + 1) % this._notifications.length;
    this.changeDetectorRef.markForCheck();
  }
  public prev(): void {
    if (!this._notifications.length) return;
    this.index = (this.index - 1 + this._notifications.length) % this._notifications.length;
    this.changeDetectorRef.markForCheck();
  }

  /*Filter method */
  private reGenerate(): void {
    const base = this._source;

    //favor important but keep original order within each group
    let prioritized = base;
    if (this.favorImportant()) {
      const imp = base.filter(n => n.important);
      const rest = base.filter(n => !n.important);
      prioritized = [...imp, ...rest];
    }

    //cap to topN
    const next = prioritized.slice(0, this.topN());

    //adjust index if needed
    const prevLen = this._notifications.length;
    this._notifications = next;
    if (this.index >= this._notifications.length) {
      this.index = this._notifications.length ? 0 : 0;
    }

    //restart ticker only when it is the case
    if (prevLen !== this._notifications.length) {
      this.ensureTicker();
    }
  }

  /** Auto-rotate ticker */
  private ensureTicker(): void {
    this.rotateSub?.unsubscribe();
    if (this._notifications.length > 1 && this.intervalMs() > 0) {
      this.rotateSub = interval(this.intervalMs()).subscribe(() => {
        if (!this.paused) this.next();
      });
    }
  }

  public onEnter(): void { this.paused = true; }
  public onLeave(): void { this.paused = this.dialogNotification !== null; }

  public openDetails(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const notification = this.current;
    if (!notification) return;
    this.dialogNotification = notification;
    this.paused = true;
    this.changeDetectorRef.markForCheck();

    if (notification.channel === NotificationChannel.FEEDBACK &&
      notification.action?.type === NotificationActionType.FEEDBACK_EDIT &&
      notification.model?.type === GenericModelType.FEEDBACK_FORM &&
      !this.capabilitiesService.capabilitiesLoaded()) {
      this.subscriptions.push(this.capabilitiesService.fetchUserCapabilities().pipe(take(1)).subscribe({
        next: (capabilities) => {
          this.capabilitiesService.applyCapabilities(capabilities);
          this.changeDetectorRef.markForCheck();
        }
      }));
    }
  }

  public closeDetails(): void {
    this.dialogNotification = null;
    this.paused = false;
    this.changeDetectorRef.markForCheck();
  }

  public toggleDialogRead(): void {
    const notification = this.dialogNotification;
    if (!notification) return;
    if (notification.read) {
      this.asUnread.emit(notification.id);
    } else {
      if (this.variant() === 'fixed') {
        this.closeDetails();
      }
      this.asRead.emit(notification.id);
    }
    if (this.dialogNotification === null) return;
    this.dialogNotification = {...notification, read: !notification.read};
    this.changeDetectorRef.markForCheck();
  }

  public goToDialogContent(): void {
    const notification = this.dialogNotification;
    if (!notification || !this.hasContentRoute(notification)) return;
    this.closeDetails();
    this.open(notification);
  }

  public hasContentRoute(notification: NotificationResource): boolean {
    if (!notification.action) return false;
    const model = notification.model;

    if (notification.channel === NotificationChannel.FEEDBACK) {
      if (!model) return false;
      if (notification.action.type === NotificationActionType.FEEDBACK_EDIT) {
        return model.type === GenericModelType.FEEDBACK_FORM && Number.isSafeInteger(model.id) && model.id > 0 &&
          this.capabilitiesService.capabilitiesLoaded() && !!this.capabilitiesService.capabilities().feedback?.manage;
      }
      if (notification.action.type === NotificationActionType.FEEDBACK_STATISTICS) {
        return model.type === GenericModelType.FEEDBACK_INSTANCE && Number.isSafeInteger(model.id) && model.id > 0 &&
          model.parent_id !== null && Number.isSafeInteger(model.parent_id) && model.parent_id > 0;
      }
      if (notification.action.type === NotificationActionType.FEEDBACK_RESULT) {
        return model.type === GenericModelType.FEEDBACK_ATTEMPT && Number.isSafeInteger(model.id) && model.id > 0;
      }
      if (notification.action.type === NotificationActionType.FEEDBACK_ATTEMPT) {
        return model.type === GenericModelType.FEEDBACK_INSTANCE && Number.isSafeInteger(model.id) && model.id > 0;
      }
      return false;
    }

    if (this.getCalendarEventIdFromNotification(notification) !== null) return true;
    if (!model) return false;
    if (model.type === GenericModelType.FEEDBACK_FORM) return true;
    return this.modConfigService.getRouteForGenericModelResource(
      model.type, String(model.id), model.parent_id === null ? null : String(model.parent_id)
    ) !== null;
  }

  public setRead(event: Event, menuTrigger: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    this.asRead.emit(this.current!.id);
    this.closeDropdown(menuTrigger);
  }
  
  public setUnRead(event: Event, menuTrigger: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    this.asUnread.emit(this.current!.id);
    this.closeDropdown(menuTrigger);
  }

  private closeDropdown(menuTrigger: HTMLElement): void {
    menuTrigger.setAttribute('aria-expanded', 'false');
    const dropdown = menuTrigger.closest('.dropdown');
    dropdown?.classList.remove('show');
    const menu = dropdown?.querySelector('.dropdown-menu');
    menu?.classList.remove('show');
  }

  public open(notification: NotificationResource | null = this.current): void {
    const n = notification;
    if (!n) return;
    if (!n.read) {
      this.asRead.emit(n.id);
    }
    if (n.action === null) return;

    if (n.channel === NotificationChannel.FEEDBACK) {
      if (n.action.type === NotificationActionType.FEEDBACK_EDIT &&
        n.model?.type === GenericModelType.FEEDBACK_FORM && !this.capabilitiesService.capabilitiesLoaded()) {
        this.subscriptions.push(this.capabilitiesService.fetchUserCapabilities().pipe(take(1)).subscribe({
          next: (capabilities) => {
            this.capabilitiesService.applyCapabilities(capabilities);
            this.navigateFeedback(n);
          }
        }));
      } else {
        this.navigateFeedback(n);
      }
      return;
    }

    const pendingCalendarEventId = this.getCalendarEventIdFromNotification(n);
    if (pendingCalendarEventId !== null) {
      this.pendingCalendarEventOpenService.setPendingEventId(pendingCalendarEventId);
      this.routerService.navigateTo('/calendar/list');
      return;
    }
    if(n.model !== null) {
      const route = this.modConfigService.getRouteForGenericModelResource(
        n.model.type, 
        String(n.model.id),
        String(n.model.parent_id)
      );
      if(route !== null) {
        this.routerService.navigateTo(route);
      }
    }
  }

  private navigateFeedback(notification: NotificationResource): void {
    const model = notification.model;
    if (model === null || notification.action === null || ![
      GenericModelType.FEEDBACK_FORM,
      GenericModelType.FEEDBACK_INSTANCE,
      GenericModelType.FEEDBACK_ATTEMPT,
    ].includes(model.type)) return;
    const route = this.modConfigService.getRouteForGenericModelResource(
      model.type,
      String(model.id),
      model.parent_id === null ? null : String(model.parent_id),
      notification.action.type
    );
    if (route !== null) this.routerService.navigateTo(route);
  }

  public stopPrevent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public ngOnDestroy(): void {
    this.rotateSub?.unsubscribe();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public createDate(date: string): Date {
    return new Date(date);
  }

  public sendCalendarRsvpYes(): void {
    this.sendCalendarEventRsvp(CalendarEventConfirmation.YES)
  }

  public sendCalendarRsvpNo(): void {
    this.sendCalendarEventRsvp(CalendarEventConfirmation.NO)
  }

  public sendCalendarRsvpMaybe(): void {
    this.sendCalendarEventRsvp(CalendarEventConfirmation.MAYBE)
  }

  private sendCalendarEventRsvp(answer: CalendarEventConfirmation): void {
    this.api.setCalendarEventRSVP(
      this.current?.model?.id ?? 0,
      {answer}
    )
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.toaster.success(
          this.lang.getTranslationByKey('SUCCESS'),
          this.lang.getTranslationByKey('CALRESPONSESETSUCCESS')
        );
        this.notificationStateService.handleSocketNotificationUpdate(
          this.current ? {id: this.current.id} : undefined
        );
      }
    })
  }

  private getCalendarEventIdFromNotification(notification: NotificationResource): number | null {
    const actionPayload = notification.action?.payload;
    const actionEventId = this.isRecord(actionPayload) ? actionPayload['event_id'] : null;
    if (typeof actionEventId === 'number') {
      return actionEventId;
    }

    if (
      notification.model?.type === GenericModelType.CALENDAR_EVENT &&
      typeof notification.model.id === 'number'
    ) {
      return notification.model.id;
    }

    return null;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
